import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Layout, List } from 'antd';
import VirtualList from 'rc-virtual-list';
import { useNavigate } from 'react-router-dom';
import { useErrorHandler } from '../../../Hooks/useErrorHandler';
import ConversationListItem from './ConversationListItem';
import ConversationsHeader from './ConversationsHeader';
import ConversationSkeleton from './ConversationSkeleton';
import useChannelSelection from '../hooks/useChannelSelection';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';
import useSocketSubscription from '../../../Hooks/useSocketSubscription';
import { Resource } from '../../../Constants/socket';
import useConversationManager from '../hooks/useConversationManager';
import useConversationAutoSelect from '../hooks/useConversationAutoSelect';
import { LAYOUT_HEIGHTS, SKELETON_COUNT } from '../constants/layout';
import { getConversations } from '../MessagesServices';
import ContactsServices from '../../Contacts/ContactsServices';

const ConversationsList = ({ onSelectConversation, selectedId, conversations, setConversations, isMobile, initialContactId }) => {
    const { isDarkMode } = useTheme();
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const { handleError } = useErrorHandler();
    const navigate = useNavigate();
    const hasLoadedInitialConversation = useRef(false);
    const deepLinkedChannelId = useRef(null);
    const listRef = useRef(null);
    const scrollContainerRef = useRef(null);

    // Anchor refs for scroll position preservation when loading older conversations
    const anchorIdRef = useRef(null);
    const anchorOffsetRef = useRef(0);
    const isPendingTopLoadRef = useRef(false);

    // Channel selection using custom hook
    const {
        channels,
        loading: loadingChannels,
        selectedChannel,
        setSelectedChannel,
        selectedChannelAgentId,
        handleSearch: onChannelSearch
    } = useChannelSelection(handleError);

    // Subscribe to the selected channel's agent to receive live updates
    // If channel doesn't have an agent, this might skip subscription or we might need another strategy

    // Subscribe to the selected channel's room.
    // The backend emits CONVERSATION events (create, update) to the CHANNEL resource room,
    // so we must subscribe to the CHANNEL resource to receive them.
    useSocketSubscription(Resource.CHANNEL, selectedChannel ? [selectedChannel] : null, true);

    // Unified conversation management - handles pagination, filtering, search, sorting, and initialization
    const {
        hasNextPage,
        hasPrevPage, // NEW: For upward scroll
        loading,
        loadingTop, // NEW: For upward scroll loading state
        handleScroll,
        sortBy,
        sortDirection,
        sortedConversations,
        handleSearchChange,
        handleFiltersChange,
        handleSortChange,
        chipFilters,
        setPaginationState // NEW: Exposure for manual updates
    } = useConversationManager({
        conversations,
        selectedChannel,
        setConversations,
        onSelectConversation,
        skipInitialLoad: !!initialContactId || (deepLinkedChannelId.current && deepLinkedChannelId.current === selectedChannel),
    });

    // Reset deep link state when channel changes to something else
    useEffect(() => {
        if (selectedChannel && deepLinkedChannelId.current && selectedChannel !== deepLinkedChannelId.current) {
            deepLinkedChannelId.current = null;
        }
    }, [selectedChannel]);

    // Wrap handleScroll to capture anchor before loading older conversations
    const handleScrollWithAnchor = useCallback((e) => {
        const { scrollTop } = e.currentTarget;

        // Before triggering top load, save anchor for position restoration
        if (scrollTop === 0 && hasPrevPage && !loadingTop && sortedConversations.length > 0) {
            // Mark that we're about to load, and save anchor
            anchorIdRef.current = sortedConversations[0]?.id;
            anchorOffsetRef.current = 0; // Top of list
            isPendingTopLoadRef.current = true;
        }

        // Call original scroll handler
        handleScroll(e);
    }, [hasPrevPage, loadingTop, sortedConversations, handleScroll]);

    // Restore scroll position after prepending older conversations
    useEffect(() => {
        if (!isPendingTopLoadRef.current) return;
        if (loadingTop) return; // Wait for loading to complete

        const container = scrollContainerRef.current;
        if (!container) return;

        const anchorId = anchorIdRef.current;
        if (!anchorId) return;

        // Find the anchor conversation in the updated list
        const anchorIndex = sortedConversations.findIndex(c => c.id === anchorId);
        if (anchorIndex !== -1) {
            // Restore scroll position using calculated position
            requestAnimationFrame(() => {
                const scrollPosition = anchorIndex * 60; // itemHeight = 60
                container.scrollTop = scrollPosition - anchorOffsetRef.current;
                console.log('DeepLink: Restored scroll after prepend', { anchorIndex, scrollPosition });
            });
        }

        // Clear pending flag
        isPendingTopLoadRef.current = false;
        anchorIdRef.current = null;
        anchorOffsetRef.current = 0;
    }, [sortedConversations, loadingTop]);

    // Auto-select logic
    useConversationAutoSelect({
        isMobile,
        selectedId,
        sortedConversations,
        onSelectConversation,
        loading,
        loadingSearch: loading
    });

    // Handle deep-linking to a specific conversation via contact
    useEffect(() => {
        if (!initialContactId || hasLoadedInitialConversation.current) {
            return;
        }

        // Fetch the conversation by contact_id
        // Fetch the conversation by contact_id
        const loadInitialConversation = async () => {
            // Prevent multiple triggers
            if (hasLoadedInitialConversation.current) return;
            hasLoadedInitialConversation.current = true;

            try {
                // Step 1: Get specific conversation
                const contactConversationResponse = await ContactsServices.getContactConversation(initialContactId);
                const targetConversation = contactConversationResponse.data;

                if (!targetConversation) {
                    throw new Error('Conversation not found');
                }

                // Step 2: Fetch list using timestamp filtering to locate it
                // We fetch newer and older conversations to center the target
                const [olderResponse, newerResponse] = await Promise.all([
                    getConversations({
                        size: 10,
                        channel_id: targetConversation.channel_id,
                        after: targetConversation.id,
                        sort_by: 'updated_time',
                        sort_direction: 'desc'
                    }),
                    getConversations({
                        size: 10,
                        channel_id: targetConversation.channel_id,
                        until: targetConversation.id,
                        sort_by: 'updated_time',
                        sort_direction: 'desc'
                    })
                ]);

                // Merge: Newer (need reverse?) -> Target -> Older
                // Assuming API returns DESC (newest first) for both?
                // If since=ID returns newer, they are likely [id+1, id+2...]. 
                // We want [id+2, id+1, target, id-1, id-2].
                // So we spread newer (and maybe reverse if needed? standard list is DESC)
                // Let's assume standard behavior:
                // Newer ones: [newest ... slightly newer]
                // Older ones: [slightly older ... oldest]

                const newer = newerResponse?.data || [];
                const older = olderResponse?.data || [];

                // 'until' is INCLUSIVE (includes target), 'after' is EXCLUSIVE
                // So 'newer' already contains targetConversation
                const combined = [...newer, ...older];
                const conversationsList = combined;
                // Mark as loaded to prevent re-fetching
                hasLoadedInitialConversation.current = true;

                // Switch to the conversation's channel if different
                if (targetConversation.channel_id && targetConversation.channel_id !== selectedChannel) {
                    setSelectedChannel(targetConversation.channel_id);
                }

                // Track that this channel was deep-linked
                deepLinkedChannelId.current = targetConversation.channel_id;

                // Set the conversation list directly with the fetched result
                // This initializes the list with the target conversation since we skipped default load
                setConversations(conversationsList);

                // Update pagination cursors for infinite scroll
                // olderResponse = items OLDER than target (downward scroll) -> use next_cursor
                // newerResponse = items NEWER than target (upward scroll) -> use prev_cursor
                setPaginationState({
                    nextCursor: olderResponse?.metadata?.next_cursor || null,
                    hasNextPage: olderResponse?.metadata?.has_next || !!olderResponse?.metadata?.next_cursor || false,
                    prevCursor: newerResponse?.metadata?.prev_cursor || null,
                    hasPrevPage: newerResponse?.metadata?.has_prev || !!newerResponse?.metadata?.prev_cursor || false
                });

                console.log('DeepLink: Cursors updated', {
                    next: olderResponse?.metadata?.next_cursor,
                    prev: newerResponse?.metadata?.prev_cursor
                });

                // Select the target conversation
                onSelectConversation(targetConversation);

                // Scroll to the target conversation using parent container
                if (scrollContainerRef.current) {
                    setTimeout(() => {
                        const container = scrollContainerRef.current;
                        const targetIndex = conversationsList.findIndex(c => c.id === targetConversation.id);

                        console.log('DeepLink: Scroll attempt', {
                            container,
                            targetIndex,
                            itemHeight: 60,
                            calculatedPosition: targetIndex * 60
                        });

                        if (targetIndex !== -1) {
                            // Calculate scroll position: index * itemHeight
                            const scrollPosition = targetIndex * 60; // itemHeight = 60
                            container.scrollTop = scrollPosition;
                            console.log('DeepLink: Scrolled to position', scrollPosition);
                        }
                    }, 300);
                }

                // Clear the query parameter
                navigate('/messages', { replace: true });

            } catch (error) {
                handleError(error);
                navigate('/messages', { replace: true });
            }
        };

        loadInitialConversation();
    }, [initialContactId, selectedChannel, setSelectedChannel, setConversations, onSelectConversation, navigate, handleError]);

    // Memoize the render function to prevent recreation on every render
    const renderConversationItem = useCallback((conversation, index) => {
        if (!conversation) return null;
        return (
            <ConversationListItem
                key={conversation.id || index}
                conversation={conversation}
                selectedId={selectedId}
                onSelectConversation={onSelectConversation}
                index={index}
                hoveredIndex={hoveredIndex}
                setHoveredIndex={setHoveredIndex}
            />
        );
    }, [selectedId, onSelectConversation, hoveredIndex]);

    // Memoize styles to prevent unnecessary recalculations
    const layoutStyle = useMemo(() => ({
        width: '100%',
        height: '100%',
        borderInlineEnd: `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}`,
        borderTop: 'none',
        background: isDarkMode ? '#141414' : '#fff',
        overflow: 'hidden'
    }), [isDarkMode]);

    const containerStyle = useMemo(() => ({
        height: LAYOUT_HEIGHTS.CONVERSATIONS_CONTAINER,
        overflow: 'hidden',
        background: isDarkMode ? '#1f1f1f' : '#fff',
        position: 'relative'
    }), [isDarkMode]);

    const virtualListStyle = useMemo(() => ({
        height: '100%',
        overflow: 'auto',
        padding: '8px 0',
    }), []);

    return (
        <Layout style={layoutStyle}>
            <ConversationsHeader
                channels={channels}
                loading={loading}
                loadingChannels={loadingChannels}
                selectedChannel={selectedChannel}
                onChannelChange={setSelectedChannel}
                onChannelSearch={onChannelSearch}
                onSearch={handleSearchChange}
                filters={chipFilters}
                onFiltersChange={handleFiltersChange}
                sortValue={{ sort_by: sortBy, sort_direction: sortDirection }}
                onSortChange={handleSortChange}
            />

            <div style={containerStyle}>
                {loading && sortedConversations.length === 0 ? (
                    <div style={{ overflow: 'auto', height: '100%' }}>
                        {Array.from({ length: SKELETON_COUNT }, (_, index) => (
                            <ConversationSkeleton key={index} />
                        ))}
                    </div>
                ) : (
                    <List
                        ref={scrollContainerRef}
                        style={{ height: '100%', overflowY: 'auto' }}
                        onScroll={handleScrollWithAnchor}
                    >
                        {loadingTop && hasPrevPage && (
                            <div style={{ padding: '8px 0', width: '100%' }}>
                                <ConversationSkeleton />
                            </div>
                        )}
                        <VirtualList
                            ref={listRef}
                            data={sortedConversations}
                            fullHeight
                            style={virtualListStyle}
                            itemHeight={60}
                            itemKey="id"
                        >
                            {renderConversationItem}
                        </VirtualList>
                        {loading && hasNextPage && (
                            <div style={{ padding: '8px 0', width: '100%' }}>
                                <ConversationSkeleton />
                            </div>
                        )}
                    </List>
                )}
            </div>
        </Layout>
    );
};

export default ConversationsList;
